import { useEffect, useState } from "react";
import ScopeRulesTable from "./ScopeRulesTable";
import { CloseButton, Form, Offcanvas } from "react-bootstrap";
import { Button as ButtonAntd } from "antd";
import { toast } from "react-toastify";

export default function RouteDrawer({
  route,
  show,
  onClose,
  onSave,
  isSubmitting,
}) {
  const isEditMode = !!route;
  const originalRoute = route || {};

  // start with empty form when in create mode
  const [form, setForm] = useState(
    route || {
      path: "",
      description: "",
      priority: 100,
      active: true,
      scopeRules: [],
    }
  );
  const [errors, setErrors] = useState({});
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) setVisible(true);
  }, [show]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const updateField = (key, value) => {
    setForm({ ...form, [key]: value });

    if (errors[key]) setErrors({ ...errors, [key]: null });
  };

  // validate form
  const validate = () => {
    const newErrors = {};
    if (!form.path.trim()) newErrors.path = "Path cannot be empty";
    return newErrors;
  };

  // check which fields changed - for patch editing
  const buildPatchPayload = (original, updated) => {
    const patches = [];

    if (updated.path !== original.path) {
      patches.push({ name: "path", value: updated.path });
    }
    if (updated.active !== original.active) {
      patches.push({ name: "active", value: updated.active });
    }
    if ((updated.description || "") !== (original.description || "")) {
      patches.push({ name: "description", value: updated.description || "" });
    }
    if ((updated.priority ?? 100) !== (original.priority ?? 100)) {
      patches.push({ name: "priority", value: updated.priority ?? 100 });
    }
    if (
      JSON.stringify(updated.scopeRules) !== JSON.stringify(original.scopeRules)
    ) {
      // strip out id from each rule before sending
      const sanitizedRules = updated.scopeRules.map(
        ({ method, scope, block }) => ({
          method,
          scope,
          block,
        })
      );

      patches.push({ name: "scopeRules", value: sanitizedRules });
    }

    return patches;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isEditMode) {
      // build payload for patching
      const patches = buildPatchPayload(originalRoute, form);

      if (patches.length === 0) {
        toast.info("No changes to save");
        onClose();
        return;
      }

      await onSave(patches, route.id);
    } else {
      // send full form in create mode
      await onSave(form);
    }
  };

  return (
    <Offcanvas
      show={visible}
      onHide={handleClose}
      placement="end"
      style={{ width: "30rem" }}
      className="border-0 d-flex flex-column"
    >
      <div className="offcanvas-header px-4">
        <h5 className="text-base fw-bold">
          {isEditMode ? "View/Edit Route" : "Create Route"}
        </h5>
        <CloseButton
          className="btn btn-circle btn-sm transition-base p-0 text-xxs"
          onClick={handleClose}
        />
      </div>

      <div className="flex-grow-1 overflow-auto px-4">
        <Form.Group className="mb-3">
          <Form.Label>Path</Form.Label>

          {isEditMode ? (
            <div
              className="font-monospace text-sm custom-shadow p-2 rounded"
              style={{
                background: "#f8f9fa",
              }}
              title={form.path}
            >
              {form.path}
            </div>
          ) : (
            <>
              <Form.Control
                className="font-monospace"
                value={form.path}
                onChange={(e) => updateField("path", e.target.value)}
                placeholder="/identity-service/api/v1/users"
                isInvalid={!!errors.path}
              />
              <Form.Control.Feedback type="invalid">
                {errors.path}
              </Form.Control.Feedback>
            </>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            value={form.description || ""}
            onChange={(e) => updateField("description", e.target.value)}
            placeholder="Route description"
          />
        </Form.Group>

        <div className="row mb-3">
          <div className="col">
            <Form.Group>
              <Form.Label>Priority</Form.Label>
              <Form.Control
                type="number"
                value={form.priority}
                onChange={(e) => updateField("priority", e.target.value)}
              />
            </Form.Group>
          </div>

          <div className="col d-flex align-items-end">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                checked={form.active}
                onChange={(e) => updateField("active", e.target.checked)}
              />
              <label className="form-check-label">Active</label>
            </div>
          </div>
        </div>

        <ScopeRulesTable
          rules={form.scopeRules}
          onChange={(rules) => updateField("scopeRules", rules)}
        />
      </div>

      <div className="border-top p-3 bg-light text-enddd">
        <ButtonAntd
          type="primary"
          size="sm"
          className="ms-2 bg-secondary"
          style={{ fontFamily: "Inter" }}
          onClick={handleClose}
        >
          Close
        </ButtonAntd>
        {isSubmitting ? (
          <ButtonAntd
            type="primary"
            loading
            iconPlacement="end"
            size="sm"
            className="ms-2 bg-success"
            style={{ fontFamily: "Inter" }}
          >
            Saving
          </ButtonAntd>
        ) : (
          <ButtonAntd
            variant="success"
            size="sm"
            onClick={handleSave}
            className="text-nowrap text-xs ms-2 bg-success text-white"
          >
            {isEditMode ? "Save Changes" : "Create Route"}
          </ButtonAntd>
        )}
      </div>
    </Offcanvas>
  );
}
